import type { IconProps } from '@carboncopyco/icons';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import type React from 'react';
import { useEffect } from 'react';
import { Spacer } from '../../Spacer';
import { TextBase } from '../../Typography';
import { menuItemBackgroundColor, menuItemTextColor } from './consts';
import type { MenuItemState } from './types';

export type MenuItemProps<T extends { id: string; label: string }> = {
  item: T;
  disabled?: boolean;
  selected?: boolean;
  focus?: boolean;
  iconLeft?: React.FC<IconProps>;
  iconRight?: React.FC<IconProps>;
  renderItem?: (item: T, state: MenuItemState) => React.ReactElement;
} & (
  | { onSelect?: never; isClickable: boolean }
  | { onSelect?: (id: string) => void; isClickable?: never }
);

export const MenuItem = <T extends { id: string; label: string }>({
  item,
  onSelect,
  isClickable = true,
  disabled,
  iconLeft,
  iconRight,
  renderItem,
  selected,
  focus,
}: MenuItemProps<T>) => {
  const [active, setActive] = useBoolean();
  const pointer = onSelect || isClickable;
  const state = disabled
    ? 'disabled'
    : selected
      ? 'selected'
      : active
        ? 'active'
        : focus
          ? 'focus'
          : 'default';
  const IconLeft = iconLeft;
  const IconRight = iconRight;

  useEffect(() => {
    document.addEventListener('mouseup', () => {
      setActive.setFalse();
    });
    return document.removeEventListener('mouseup', () => {
      setActive.setFalse();
    });
  }, []);

  return (
    <div
      role="menuitem"
      onMouseDown={setActive.setTrue}
      onClick={() => onSelect?.(item.id)}
      onKeyDown={(event) => {
        if (event.code === 'Enter') {
          onSelect?.(item.id);
        }
      }}
    >
      {renderItem?.(item, state) ?? (
        <div
          className={classNames(
            'flex items-center justify-between rounded px-2.5 py-2 w-full',
            menuItemTextColor[state],
            menuItemBackgroundColor[state],
            {
              'cursor-pointer': pointer && !disabled,
              'cursor-default': !pointer || disabled,
            }
          )}
        >
          <div className="flex items-center gap-2">
            <div className={menuItemTextColor[state]}>
              {IconLeft && <IconLeft />}
            </div>
            <div className="truncate">
              <TextBase
                label={item.label}
                color={menuItemTextColor[state]}
                ellipsis
              />
            </div>
            <Spacer amount={0.75} />
          </div>
          <div className={menuItemTextColor[state]}>
            {IconRight && <IconRight />}
          </div>
        </div>
      )}
    </div>
  );
};
